import React from 'react'
import { Button, message, Table, Popconfirm } from 'antd'
import snakeCase from 'lodash/snakeCase'
import { useMutation, useApolloClient } from '@apollo/client'
import { DeleteOutlined } from '@ant-design/icons'
import { ProductVariant } from './types'
import { ARCHIVE_VARIANT_OPTION } from './VariantQueries'
import VariantNameAndAvatar from 'components/Widgets/VariantNameAndAvatar'
import { GroupVariantsByOptionName } from './utils'

interface VariantGroupTableProps {
  productId: string
  baseName: string
  variants: ProductVariant[]
  variantOptions: string[]
  variantGroupName: string
}

interface VariantTableRow {
  name: string
  option: string
}

const VariantGroupTable = ({
  productId,
  baseName,
  variants,
  variantOptions,
  variantGroupName
}: VariantGroupTableProps) => {
  const client = useApolloClient()
  const [archiveVariantOption, { loading: archivingVariantOption }] =
    useMutation(ARCHIVE_VARIANT_OPTION)
  const variantsByOptionName = GroupVariantsByOptionName(
    variantOptions,
    variantGroupName,
    variants
  )
  const variantDatasource = variantOptions
    .filter(
      (option: string) => !!variantsByOptionName[snakeCase(option)].length
    )
    .map((option: string) => ({
      name: baseName,
      option
    }))

  const archiveVariantsByOptionName = (optionName: string) => {
    message.destroy()
    message.loading('Archiving product variant group options...', 3)

    archiveVariantOption({
      variables: {
        optionName: variantGroupName,
        optionValue: optionName,
        productId
      }
    })
      .then((result) => {
        message.destroy()
        message.success('Product variant group option acrhived.', 3)
        client.resetStore()
      })
      .catch((error) => {
        message.destroy()
        message.error(
          `Unable to arhive product variants due to ${error.message}`,
          3
        )
      })
  }

  const columns = [
    {
      title: 'Variants',
      key: 'key',
      render: ({ option }: VariantTableRow) => {
        return (
          <>
            <VariantNameAndAvatar
              id={variants[0].id}
              name={baseName}
              testId='variant-group-row-avatar'
              options={[option]}
            />
          </>
        )
      }
    },
    {
      title: 'Archive',
      key: 'archive',
      align: 'right',
      render: ({ option }: VariantTableRow) => {
        return (
          <Popconfirm
            title='Are you sure you want to archive this variant group option?'
            okText='Yes'
            cancelButtonProps={{ type: 'ghost' }}
            cancelText='No'
            onConfirm={() => archiveVariantsByOptionName(option)}
          >
            <Button
              data-testid='variant-group-option-row-archive-btn'
              className='-gray-bordered'
              loading={archivingVariantOption}
              disabled={archivingVariantOption}
              ghost
              size='small'
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        )
      }
    }
  ]

  return (
    <div data-test='variant-group-options-table'>
      <Table
        columns={columns}
        dataSource={variantDatasource}
        rowKey='id'
        pagination={false}
      />
    </div>
  )
}

export default VariantGroupTable
